import React, { useState, useMemo, useCallback, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { makeStyles, Field, Avatar, shorthands } from "@fluentui/react-components";
import { DownloadDynamicFormFieldFile, SubmitTaskFormDueDates } from "../../../../../services/api/task-telerik-api";
import { getFormattedDate, adjustDateForPicker, DateStringFormatBasedOnDepartment } from "../../../../../util/time-helpers";
import { LoginContext } from "../../../../../services/api/authentication-api";
import ActionableMembersDropdown from "../common-components/actionable-members-dropdown";
import { Badge } from "@fluentui/react-components";
import { ClipboardPasteRegular as PasteIcon } from "@fluentui/react-icons";
import StatusBadge from "../../../../admin/scenes/workflow/create-edit-workflow/subcomponents/StatusBadge";
import { FluentTooltip } from "../../../../../components/FluentUI/Fluent-Tooltip";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { Button } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { TaskStatusTypes } from "../../tasks-actions-panel/task-status-types";

// Gray badge color configuration for artifacts
const ARTIFACT_BADGE_COLOR = {
  BackgroundColor: "#f0f0f0",
  FontColor: "#555555",
  BorderColor: "#bfbfbf",
};

// Helper function to parse dates
const parseDate = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) return dateValue;
  const parsed = new Date(dateValue);
  return isNaN(parsed.getTime()) ? null : parsed;
};

// Styles for inline date picker in table cells
const useDatePickerStyles = makeStyles({
  root: {
    width: "100%",
    "& .fui-CalendarDay": {
      "& table": {
        "& tr": {
          height: "0px",
          ...shorthands.border("none"),
        },
      },
    },
  },
});

const TaskFormsDetailGrid = ({ forms, taskId, onSave, showToast, onSaveReady, isArchived, taskStatusId }) => {
  const datePickerStyles = useDatePickerStyles();
  const user = useContext(LoginContext);
  const labels = user?.currentUser?.objectLabels || {};
  const userId = user?.userID;
  const userRole = user?.currentUser?.profile?.strRole || user?.currentUser?.profile?.StrRole || "";
  const isBPMUser = userRole === "RBC-CM--Clear-S&D-Business_Project_Manager";
  const dateFormatId = user?.currentUser?.department?.DateFormatID;
  const canEdit = (user?.currentUser?.privileges?.["Task Management"]?.Edit ?? false);

  const initializeFormData = useCallback((formsList) => {
    return formsList?.map((form) => {
      const dueDate = parseDate(form.DueDate || form.Due);
      return {
        ...form,
        originalDue: dueDate,
        editedDue: dueDate,
      };
    }) || [];
  }, []);

  const [formData, setFormData] = useState(() => initializeFormData(forms));
  const [originalFormData, setOriginalFormData] = useState(() => initializeFormData(forms));
  const [isSaving, setIsSaving] = useState(false);

  // Update formData when forms prop changes
  useEffect(() => {
    const newFormData = initializeFormData(forms);
    setFormData(newFormData);
    setOriginalFormData(newFormData);
  }, [forms, initializeFormData]);

  const isDueDatePast = useCallback((date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(date);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  }, []);
  const history = useHistory();
  const openForm = (ev, id) => {
    ev.preventDefault();
    history.push(`/tasks/${taskId}/form/${id}`, { fromTaskList: true });
  };

  const hasChanges = useMemo(() => {
    return formData.some((form, index) => {
      const original = originalFormData[index];
      if (!original) return false;
      const originalTime = original.originalDue?.getTime() || null;
      const editedTime = form.editedDue?.getTime() || null;
      return originalTime !== editedTime;
    });
  }, [formData, originalFormData]);

  const handleDueDateChange = useCallback((formId, newDate) => {
    // Only update if newDate is a valid Date object
    // This prevents unnecessary re-renders when the picker opens/closes without selection
    if (newDate === null || newDate === undefined) {
      return; // Don't update state if no date selected
    }

    // Ensure newDate is a valid Date
    if (!(newDate instanceof Date) || isNaN(newDate.getTime())) {
      return;
    }

    setFormData((prev) =>
      prev.map((form) => {
        if (form.Id === formId || form.FormId === formId) {
          // Only update if the date actually changed
          const currentTime = form.editedDue?.getTime() || null;
          const newTime = newDate?.getTime() || null;
          if (currentTime === newTime) {
            return form; // No change, return as-is
          }
          return { ...form, editedDue: newDate };
        }
        return form;
      })
    );
  }, []);

  // Helper to format date for payload based on department's DateFormatID
  const formatDateForPayload = useCallback((date) => {
    if (!date || !(date instanceof Date) || isNaN(date)) return "";
    try {
      return DateStringFormatBasedOnDepartment(date, dateFormatId);
    } catch {
      return "";
    }
  }, [dateFormatId]);

  const handleSave = useCallback(async () => {
    if (!hasChanges) return;

    setIsSaving(true);
    try {
      // Build payload for ALL forms (backend expects all form due dates)
      const payload = formData.map((form) => ({
        TaskId: taskId,
        FormId: form.FormId,
        DynamicFieldId: form.DueDateDynamicFieldId,
        FieldType: "Date",
        Data: formatDateForPayload(form.editedDue),
        ModifiedById: userId,
      }));

      await SubmitTaskFormDueDates(payload);

      showToast(
        "Success",
        "Due dates updated successfully",
        "success"
      );

      // Update original data to reflect saved state
      setFormData((prev) => {
        const updated = prev.map((form) => ({
          ...form,
          originalDue: form.editedDue,
        }));
        setOriginalFormData(updated);
        return updated;
      });

      // Refresh parent if callback provided
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("Error saving form due dates:", error);
      showToast(
        "Error",
        error?.response?.data?.message || "Failed to save due dates",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  }, [formData, taskId, userId, hasChanges, formatDateForPayload, showToast, onSave]);

  // Expose save state and handler to parent component
  // Use a ref to avoid infinite loops - only update when hasChanges or isSaving changes
  const handleSaveRef = React.useRef(handleSave);

  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  useEffect(() => {
    if (onSaveReady) {
      onSaveReady({
        hasChanges,
        handleSave: () => handleSaveRef.current(),
        isSaving,
      });
    }
    // Only depend on hasChanges and isSaving to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanges, isSaving]);

  const renderActionableMembers = useCallback((members) => {
    if (!members || members.length === 0) return <span></span>;

    const MAX_LENGTH = 26;
    // Helper to trim text to max characters
    const trimText = (text) => text?.length > MAX_LENGTH ? `${text.substring(0, MAX_LENGTH)}...` : text;
    // Helper to check if text is truncated
    const isTruncated = (text) => text?.length > MAX_LENGTH;

    // For 2 or fewer members, show inline with avatar, name, and role
    if (members.length <= 2) {
      return (
        <div className="d-flex align-items-center flex-wrap">
          {members.map((member) => {
            const needsTooltip = isTruncated(member.Name) || isTruncated(member.RoleName);
            const tooltipContent = member.RoleName
              ? (<div><div>{member.Name}</div><div style={{ fontSize: "11px", color: "#666" }}>{member.RoleName}</div></div>)
              : member.Name;

            const content = (
              <div className="ml-1 d-flex flex-column">
                <span>{trimText(member.Name)}</span>
                {member.RoleName && (
                  <span style={{ fontSize: "10px", color: "#666" }}>{trimText(member.RoleName)}</span>
                )}
              </div>
            );

            return (
              <div key={member.Id} className="d-flex align-items-center mr-2 mb-1">
                <Avatar
                  size={20}
                  name={member.Name}
                  color="blue"
                />
                {needsTooltip ? (
                  <FluentTooltip target={content} text={tooltipContent} />
                ) : (
                  content
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // For more than 2 members, use the dropdown component
    return <ActionableMembersDropdown members={members} />;
  }, []);

  // Render artifacts (files and URLs) with gray badges
  const renderArtifacts = useCallback((artifacts) => {
    if (!artifacts || artifacts.length === 0) return <span></span>;

    const MAX_NAME_LENGTH = 25;
    const trimName = (name) => name?.length > MAX_NAME_LENGTH ? `${name.substring(0, MAX_NAME_LENGTH)}...` : name;

    const handleFileDownload = async (artifact) => {

      let fileResult = await DownloadDynamicFormFieldFile(artifact.DynamicFormFieldFile.Id);
      if (fileResult.status == 200) {
        const file = fileResult.data;
        if (!file?.FileString || !file?.MimeType) return;

        // Create blob from base64 file string and trigger download
        try {
          const byteCharacters = atob(file.FileString);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: file.MimeType });

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = file.Name || artifact.FieldName || "download";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Error downloading file:", error);
        }
      }
      else {
        console.error("Error downloading file:", fileResult);
      }
    };

    const handleUrlOpen = (url) => {
      if (!url) return;
      // Ensure URL has protocol
      const formattedUrl = url.startsWith("http://") || url.startsWith("https://")
        ? url
        : `https://${url}`;
      window.open(formattedUrl, "_blank", "noopener,noreferrer");
    };

    return (
      <div className="d-flex align-items-center flex-wrap" style={{ gap: "4px" }}>
        {artifacts.map((artifact, index) => {
          const isFile = artifact.FieldType?.toLowerCase() === "file";
          const isUrl = artifact.FieldType?.toLowerCase() === "url";

          let displayName = "Artifact";
          const needsTooltip = displayName.length > MAX_NAME_LENGTH;

          if (isUrl) {
            displayName = artifact.FieldName || "Link";
          } else if (isFile) {
            displayName = artifact.FieldName || artifact.FileName || artifact.DynamicFormFieldFile?.Name || "File";
          }

          // Build status object for StatusBadge
          const status = {
            Name: trimName(displayName),
            BadgeColor: ARTIFACT_BADGE_COLOR,
          };

          const handleClick = (e) => {
            e.stopPropagation();
            if (isFile) {
              handleFileDownload(artifact);
            } else if (isUrl) {
              handleUrlOpen(artifact.FieldData);
            }
          };

          const badgeContent = (
            <div
              onClick={handleClick}
              style={{
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Badge appearance="tint" color="informative" icon={<PasteIcon />}>
                {status?.Name ?? displayName}
              </Badge>
            </div>
          );

          return (
            <div key={artifact.FormFieldId || index}>
              {needsTooltip ? (
                <FluentTooltip target={badgeContent} text={displayName} />
              ) : (
                badgeContent
              )}
            </div>
          );
        })}
      </div>
    );
  }, []);

  if (!forms || forms.length === 0) {
    return <div className="p-3">No sub-tasks available for this task.</div>;
  }

  return (
    <div className="task-forms-detail-container">
      <Grid data={formData} style={{ height: "65vh" }}>
        <Column
          field="DisplayOrder"
          title="#"
          editable={false}
          width={50}
          cell={(props) => (
            <td className="k-table-td">{props.dataItem?.DisplayOrder || ""}</td>
          )}
        />
        <Column
          field="FormName"
          title={labels?.FormSingular || "Sub-Task"}
          editable={false}
          width={245}
          cell={(props) => (
            <td className="k-table-td">{props.dataItem?.FormName || "—"}</td>
          )}
        />
        <Column
          field="FormStatusName"
          title="Status"
          editable={false}
          width={145}
          cell={(props) => {
            const badgeColor = props.dataItem?.BadgeColor;
            const formStatus = props.dataItem?.FormStatusName;
            // Build status object with BadgeColor from form status
            const status = formStatus ? {
              Name: formStatus || props.dataItem?.FormStatusName,
              BadgeColor: badgeColor
            } : { Name: props.dataItem?.FormStatusName || "—" };

            return (
              <td className="k-table-td">
                <StatusBadge status={status} />
              </td>
            );
          }}
        />
        <Column
          field="DueDate"
          title="Due Date"
          width={175}
          editable={isBPMUser}
          cell={(props) => {
            const form = props.dataItem;
            const hasError = isDueDatePast(form.editedDue) && !form.IsCompleted;
            const datePickerId = `due-date-${form.Id || form.FormId || form.TaskId || 'unknown'}`;
            // Use undefined instead of null for DatePicker when no date is set
            // FluentUI DatePicker handles undefined better than null for empty values
            const dateValue = form.editedDue ? adjustDateForPicker(form.editedDue) : undefined;

            return (
              <td className="k-table-td">
                <Field
                  className="d-block"
                  validationMessage={hasError ? "Due date is in the past" : undefined}
                >
                  <DatePicker
                    id={datePickerId}
                    name={datePickerId}
                    placeholder="Select a due date"
                    formatDate={getFormattedDate}
                    value={dateValue}
                    disabled={
                      !isBPMUser
                      || isArchived
                      || taskStatusId === TaskStatusTypes.COMPLETED
                      || taskStatusId === TaskStatusTypes.APPROVED
                      || taskStatusId === TaskStatusTypes.CANCELLED
                    }
                    onSelectDate={(date) => handleDueDateChange(form.Id || form.FormId, date)}
                    className={`${datePickerStyles.root} ${hasError ? 'error' : ''}`}
                    allowTextInput={false}
                    calendar={{ className: datePickerStyles.root }}
                  />
                </Field>
              </td>
            );
          }}
        />
        <Column
          field="ActionableMembers"
          title="Actionable by"
          width={310}
          cell={(props) => (
            <td className="k-table-td">
              {renderActionableMembers(props.dataItem?.ActionableBy)}
            </td>
          )}
        />
        <Column
          field="Artifacts"
          title="Artifacts"
          width={300}
          cell={(props) => (
            <td className="k-table-td">
              {renderArtifacts(props.dataItem?.Artifacts)}
            </td>
          )}
        />
        <Column
          field="IsFormDisable"
          title="Action"
          width={70}
          cell={(props) => {
            const isDisabled = props.dataItem?.IsFormDisable || isArchived;
            return (
              <td className="k-table-td">
                <Button
                  variant="link"
                  onClick={(ev) => openForm(ev, props.dataItem.FormId)}
                  disabled={isDisabled}
                >
                  <FluentUiIcon
                    name="EditRegular"
                    size={20}
                    color={!isDisabled ? "#0F6CBD" : "#BDBDBD"}
                  />
                </Button>
              </td>
            );
          }}
        />
      </Grid>
    </div>
  );
};

TaskFormsDetailGrid.propTypes = {
  forms: PropTypes.arrayOf(
    PropTypes.shape({
      Id: PropTypes.any.isRequired,
      FormId: PropTypes.any,
      TaskId: PropTypes.number,
      FormName: PropTypes.string,
      FormStatusName: PropTypes.string,
      Due: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      ActionableMembers: PropTypes.arrayOf(
        PropTypes.shape({
          Id: PropTypes.number,
          Name: PropTypes.string,
          RoleName: PropTypes.string,
        })
      ),
      Artifacts: PropTypes.arrayOf(
        PropTypes.shape({
          FormId: PropTypes.string,
          FormFieldId: PropTypes.string,
          FieldType: PropTypes.string,
          FieldName: PropTypes.string,
          FieldData: PropTypes.string,
          DynamicFormFieldFile: PropTypes.shape({
            Id: PropTypes.string,
            CompanyId: PropTypes.number,
            ObjectId: PropTypes.number,
            DynamicFieldId: PropTypes.number,
            Name: PropTypes.string,
            FileString: PropTypes.string,
            MimeType: PropTypes.string,
            CreatedAt: PropTypes.string,
            CreatedBy: PropTypes.number,
            UpdatedAt: PropTypes.string,
            UpdatedBy: PropTypes.number,
            FileStatus: PropTypes.number,
          }),
        })
      ),
    })
  ).isRequired,
  taskId: PropTypes.number.isRequired,
  onSave: PropTypes.func,
  showToast: PropTypes.func.isRequired,
  onSaveReady: PropTypes.func,
  isArchived: PropTypes.bool,
  taskStatusId: PropTypes.number,
};

export default TaskFormsDetailGrid;
